import { useMemo, useState } from "react";
import type { AdminUserDto, RoleDto } from "@codestack/shared";
import { createUserSchema } from "@codestack/shared";
import { Badge, Button, Input, Modal, Select, StatusBadge } from "@codestack/ui";
import { apiRequest, ApiError } from "../lib/api";
import { useApi } from "../lib/useApi";
import { useAuth } from "../lib/auth";
import { EmptyState, ErrorState, InlineAlert, LoadingState } from "../components/States";
import { dateTime, initials, relativeTime, titleCase } from "../lib/format";

type CreateForm = {
  name: string;
  email: string;
  password: string;
  roleSlugs: string[];
  status: "ACTIVE" | "SUSPENDED";
};

const emptyForm: CreateForm = {
  name: "",
  email: "",
  password: "",
  roleSlugs: ["VIEWER"],
  status: "ACTIVE",
};

export function TeamPage() {
  const { user: currentUser, can } = useAuth();
  const users = useApi<AdminUserDto[]>("/admin/users");
  const roles = useApi<RoleDto[]>("/admin/roles");

  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<CreateForm>(emptyForm);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [editing, setEditing] = useState<AdminUserDto | null>(null);
  const [editRoles, setEditRoles] = useState<string[]>([]);
  const [editStatus, setEditStatus] = useState<"ACTIVE" | "SUSPENDED">("ACTIVE");
  const [editError, setEditError] = useState<string | null>(null);
  const [editSaving, setEditSaving] = useState(false);

  const canManage = can("user:manage");
  const roleOptions = roles.data ?? [];

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return users.data ?? [];
    return (users.data ?? []).filter(
      (entry) =>
        entry.name.toLowerCase().includes(term) || entry.email.toLowerCase().includes(term),
    );
  }, [users.data, search]);

  const openCreate = () => {
    setForm(emptyForm);
    setFieldErrors({});
    setFormError(null);
    setCreateOpen(true);
  };

  const submitCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);
    setFieldErrors({});

    const parsed = createUserSchema.safeParse({
      name: form.name.trim(),
      email: form.email.trim(),
      password: form.password,
      roleSlugs: form.roleSlugs,
      status: form.status,
    });

    if (!parsed.success) {
      const errors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? "form");
        if (!errors[key]) errors[key] = issue.message;
      }
      setFieldErrors(errors);
      return;
    }

    setSubmitting(true);
    try {
      await apiRequest("/admin/users", { method: "POST", body: parsed.data });
      setCreateOpen(false);
      users.reload();
    } catch (cause) {
      setFormError(
        cause instanceof ApiError ? cause.message : "The user could not be created.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const openEdit = (entry: AdminUserDto) => {
    setEditing(entry);
    setEditRoles(entry.roles);
    setEditStatus(entry.status);
    setEditError(null);
  };

  const saveEdit = async () => {
    if (!editing) return;
    setEditSaving(true);
    setEditError(null);
    try {
      await apiRequest(`/admin/users/${editing.id}`, {
        method: "PATCH",
        body: { roleSlugs: editRoles, status: editStatus },
      });
      setEditing(null);
      users.reload();
    } catch (cause) {
      setEditError(
        cause instanceof ApiError ? cause.message : "The user could not be updated.",
      );
    } finally {
      setEditSaving(false);
    }
  };

  const toggleRole = (slug: string) => {
    setForm((current) => ({
      ...current,
      roleSlugs: current.roleSlugs.includes(slug)
        ? current.roleSlugs.filter((value) => value !== slug)
        : [...current.roleSlugs, slug],
    }));
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-display text-xl font-bold text-white">Team</h1>
          <p className="text-sm text-ink-400">
            Admin accounts and the roles that grant their permissions.
          </p>
        </div>
        {canManage ? <Button onClick={openCreate}>Invite user</Button> : null}
      </div>

      <div className="admin-card p-4">
        <Input
          placeholder="Search by name or email"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      {users.error ? <ErrorState message={users.error} onRetry={users.reload} /> : null}
      {users.loading && !users.data ? <LoadingState label="Loading team…" /> : null}

      {users.data && filtered.length === 0 ? (
        <EmptyState
          title="No users found"
          description="Invite a teammate to give them access to the admin console."
        />
      ) : null}

      {filtered.length > 0 ? (
        <div className="table-shell">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="table-head">
                <tr>
                  <th className="px-4 py-3 font-semibold">User</th>
                  <th className="px-4 py-3 font-semibold">Roles</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Last login</th>
                  <th className="px-4 py-3 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.06]">
                {filtered.map((entry) => (
                  <tr key={entry.id} className="hover:bg-white/[0.02]">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-brand-500/15 text-[11px] font-bold text-brand-200">
                          {initials(entry.name)}
                        </span>
                        <div className="min-w-0">
                          <p className="truncate text-xs font-semibold text-ink-100">
                            {entry.name}
                            {entry.id === currentUser?.id ? (
                              <span className="ml-2 text-[10px] text-ink-500">(you)</span>
                            ) : null}
                          </p>
                          <p className="truncate text-[11px] text-ink-500">{entry.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {entry.roles.map((role) => (
                          <Badge key={role} tone="brand">
                            {titleCase(role)}
                          </Badge>
                        ))}
                        {entry.roles.length === 0 ? (
                          <span className="text-[11px] text-ink-600">No roles</span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={entry.status} />
                    </td>
                    <td className="px-4 py-3 text-[11px] text-ink-500">
                      {entry.lastLoginAt ? relativeTime(entry.lastLoginAt) : "Never"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {canManage ? (
                        <Button variant="ghost" size="sm" onClick={() => openEdit(entry)}>
                          Manage
                        </Button>
                      ) : (
                        <span className="text-[11px] text-ink-600">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Invite admin user"
        description="The user can sign in immediately with the password you set."
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={() => setCreateOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" form="create-user-form" disabled={submitting}>
              {submitting ? "Creating…" : "Create user"}
            </Button>
          </>
        }
      >
        <form id="create-user-form" onSubmit={submitCreate} className="space-y-4">
          {formError ? <InlineAlert tone="danger">{formError}</InlineAlert> : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Full name"
              value={form.name}
              error={fieldErrors.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
              required
            />
            <Input
              label="Email"
              type="email"
              value={form.email}
              error={fieldErrors.email}
              onChange={(event) => setForm({ ...form, email: event.target.value })}
              required
            />
          </div>

          <Input
            label="Temporary password"
            type="password"
            value={form.password}
            error={fieldErrors.password}
            onChange={(event) => setForm({ ...form, password: event.target.value })}
            hint="Minimum 12 characters. The user should change it after signing in."
            required
          />

          <div>
            <p className="mb-2 text-sm font-medium text-ink-800">Roles</p>
            <div className="grid gap-2 sm:grid-cols-2">
              {roleOptions.map((role) => (
                <label
                  key={role.slug}
                  className="flex cursor-pointer items-start gap-2 rounded-lg border border-white/[0.08] p-3 hover:bg-white/[0.03]"
                >
                  <input
                    type="checkbox"
                    checked={form.roleSlugs.includes(role.slug)}
                    onChange={() => toggleRole(role.slug)}
                    className="mt-0.5 h-3.5 w-3.5 rounded border-white/20 bg-transparent"
                  />
                  <span>
                    <span className="block text-xs font-semibold text-ink-100">{role.name}</span>
                    <span className="block text-[11px] text-ink-500">
                      {role.description ?? `${role.permissions.length} permissions`}
                    </span>
                  </span>
                </label>
              ))}
            </div>
            {fieldErrors.roleSlugs ? (
              <p className="mt-1.5 text-xs text-red-400">{fieldErrors.roleSlugs}</p>
            ) : null}
          </div>

          <Select
            label="Status"
            value={form.status}
            onChange={(event) =>
              setForm({ ...form, status: event.target.value as CreateForm["status"] })
            }
          >
            <option value="ACTIVE">Active</option>
            <option value="SUSPENDED">Suspended</option>
          </Select>
        </form>
      </Modal>

      <Modal
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        title={editing ? `Manage ${editing.name}` : "Manage user"}
        description="Role changes take effect on the user's next request."
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)} disabled={editSaving}>
              Cancel
            </Button>
            <Button onClick={saveEdit} disabled={editSaving}>
              {editSaving ? "Saving…" : "Save changes"}
            </Button>
          </>
        }
      >
        {editing ? (
          <div className="space-y-4">
            {editError ? <InlineAlert tone="danger">{editError}</InlineAlert> : null}

            <div className="rounded-lg border border-white/[0.07] p-3 text-xs text-ink-400">
              <p className="text-ink-200">{editing.email}</p>
              <p className="mt-1">Created {dateTime(editing.createdAt)}</p>
            </div>

            <div>
              <p className="mb-2 text-sm font-medium text-ink-800">Roles</p>
              <div className="space-y-2">
                {roleOptions.map((role) => (
                  <label
                    key={role.slug}
                    className="flex cursor-pointer items-center gap-2 rounded-lg border border-white/[0.08] px-3 py-2 hover:bg-white/[0.03]"
                  >
                    <input
                      type="checkbox"
                      checked={editRoles.includes(role.slug)}
                      onChange={() =>
                        setEditRoles((current) =>
                          current.includes(role.slug)
                            ? current.filter((value) => value !== role.slug)
                            : [...current, role.slug],
                        )
                      }
                      className="h-3.5 w-3.5 rounded border-white/20 bg-transparent"
                    />
                    <span className="text-xs text-ink-200">{role.name}</span>
                  </label>
                ))}
              </div>
            </div>

            <Select
              label="Status"
              value={editStatus}
              onChange={(event) =>
                setEditStatus(event.target.value as "ACTIVE" | "SUSPENDED")
              }
              disabled={editing.id === currentUser?.id}
            >
              <option value="ACTIVE">Active</option>
              <option value="SUSPENDED">Suspended</option>
            </Select>
            {editing.id === currentUser?.id ? (
              <p className="text-[11px] text-ink-500">
                You cannot suspend your own account.
              </p>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
